/**
 * Aspect — Marketing landing page (dashboard design language)
 */

import "./landing.css";
import { icon } from "../portal/views/icons.js";
import { sparkline, trendChart, legend } from "../portal/views/charts.js";
import { t, langToggle } from "../portal/i18n.js";
import { KPIS, TREND } from "../portal/mock_data.js";

const PORTAL = "./portal.html";

const FEATURES = [
  { icon: "spaces", title: "Plan to 3D in minutes", body: "Upload a floor plan · trace rooms · publish a walkable tour. No CAD skills required." },
  { icon: "sparkles", title: "AI furniture staging", body: "Furnish empty rooms with one prompt. Real catalog items · confidence scores · instant restaging." },
  { icon: "eye", title: "First-person tours", body: "Share a link clients open on any device. They walk through the space like a video game." },
  { icon: "chart", title: "Buyer analytics", body: "See where viewers linger · when they visit · which rooms drive in-person bookings." },
  { icon: "users", title: "Team workspaces", body: "Editors · viewers · admins with roles · audit logs · client organizations." },
  { icon: "plug", title: "API & integrations", body: "Webhooks · REST API · CRM hooks to plug Aspect into your sales stack." },
];

const STEPS = [
  { title: "Upload & trace", body: "Drop a PDF or image floor plan. Draw walls and openings in the 2D editor. 3D syncs live." },
  { title: "Stage with AI", body: "Pick a style · run staging · tweak placements · publish when you're ready." },
  { title: "Share & measure", body: "Send a tour link. Track sessions · heatmaps · repeat visits from your dashboard." },
];

function kpiMini(k) {
  const deltaCls = k.dir === "up" ? "ap-delta--up" : k.good ? "ap-delta--up" : "ap-delta--down";
  const deltaIcon = k.dir === "up" ? "arrowUpRight" : "arrowDownRight";
  return `
    <div class="ap-panel ap-kpi">
      <div class="ap-kpi__top">
        <span class="ap-kpi__label">${t(k.label)}</span>
        <span class="ap-kpi__icon">${icon(k.icon, 14)}</span>
      </div>
      <div class="ap-kpi__value">${k.value}</div>
      <div class="ap-kpi__meta">
        <span class="ap-delta ${deltaCls}">${icon(deltaIcon)}${Math.abs(k.delta)}%</span>
      </div>
      <div class="ap-kpi__spark">${sparkline(k.spark, { w: 72, h: 24 })}</div>
    </div>`;
}

function previewPanel() {
  const trend = {
    ...TREND,
    labels: TREND.labels.map((l) => t(l)),
    series: TREND.series.map((s) => ({ ...s, name: t(s.name) })),
  };
  return `
    <div class="landing-preview" aria-hidden="true">
      <div class="landing-preview__chrome">
        <span class="landing-preview__dot"></span>
        <span class="landing-preview__dot"></span>
        <span class="landing-preview__dot"></span>
        <span class="landing-preview__title">${t("Aspect Overview")}</span>
      </div>
      <div class="landing-preview__body">
        <div class="landing-preview__kpis">
          ${KPIS.slice(0, 2).map(kpiMini).join("")}
        </div>
        <div class="landing-preview__chart">
          <div class="landing-preview__chart-head">
            <span>${t("Engagement trend")}</span>
            ${legend(trend.series)}
          </div>
          ${trendChart(trend, { w: 460, h: 150, gridLines: 3 })}
        </div>
      </div>
    </div>`;
}

export function renderLanding(root) {
  root.innerHTML = `
    <div class="landing">
      <header class="landing-nav">
        <a class="landing-nav__brand" href="./landing.html">
          <span class="landing-nav__mark">A</span>
          <span class="landing-nav__name">Aspect</span>
        </a>
        <nav class="landing-nav__links" aria-label="${t("Landing navigation")}">
          <a href="#features">${t("Features")}</a>
          <a href="#how">${t("How it works")}</a>
          <a href="#metrics">${t("Results")}</a>
        </nav>
        <div class="landing-nav__actions">
          ${langToggle()}
          <a class="ap-btn ap-btn--ghost ap-btn--sm" href="${PORTAL}#/login">${t("Sign in")}</a>
          <a class="ap-btn ap-btn--primary ap-btn--sm" href="${PORTAL}#/register">${t("Start free")}</a>
        </div>
      </header>

      <section class="landing-hero">
        <div class="landing-hero__glow" aria-hidden="true"></div>
        <div class="landing-hero__inner">
          <div>
            <h1>${t("Stage apartments in 3D.<br />Know what buyers see.")}</h1>
            <p class="landing-hero__lead">
              ${t("Trace floor plans · furnish with AI · publish walkable tours. Then track where buyers look · when they visit · what closes the deal.")}
            </p>
            <div class="landing-hero__ctas">
              <a class="ap-btn ap-btn--primary" href="${PORTAL}#/register">${icon("plus", 15)} ${t("Create free workspace")}</a>
              <a class="ap-btn" href="${PORTAL}#/login">${t("Open studio")}</a>
            </div>
            <div class="landing-hero__trust">
              <span>${icon("check", 13)} ${t("No credit card")}</span>
              <span>${icon("check", 13)} ${t("14-day trial")}</span>
              <span>${icon("check", 13)} ${t("Cancel anytime")}</span>
            </div>
          </div>
          ${previewPanel()}
        </div>
      </section>

      <section class="landing-kpi-row" id="metrics" aria-label="${t("Platform metrics")}">
        ${KPIS.map(kpiMini).join("")}
      </section>

      <section class="landing-section landing-section--muted" id="features">
        <div class="landing-section__inner">
          <div class="landing-section__head">
            <h2>${t("Everything your team needs to sell spaces")}</h2>
            <p>${t("From empty floor plan to client-ready tour. Staging · sharing · analytics in one glass-clear workspace.")}</p>
          </div>
          <div class="landing-features">
            ${FEATURES.map(
              (f) => `
              <article class="landing-feature">
                <div class="landing-feature__icon">${icon(f.icon, 18)}</div>
                <h3>${t(f.title)}</h3>
                <p>${t(f.body)}</p>
              </article>`
            ).join("")}
          </div>
        </div>
      </section>

      <section class="landing-section" id="how">
        <div class="landing-section__inner">
          <div class="landing-section__head">
            <h2>${t("Three steps to a published tour")}</h2>
            <p>${t("Most teams ship their first staged apartment in under an hour.")}</p>
          </div>
          <div class="landing-steps">
            ${STEPS.map(
              (s, i) => `
              <article class="landing-step">
                <span class="landing-step__n">${i + 1}</span>
                <h3>${t(s.title)}</h3>
                <p>${t(s.body)}</p>
              </article>`
            ).join("")}
          </div>
        </div>
      </section>

      <section class="landing-cta">
        <div class="landing-cta__panel">
          <div>
            <h2>${t("Ready to stage your first space?")}</h2>
            <p>${t("Join teams using Aspect to close faster with immersive tours and buyer intelligence.")}</p>
          </div>
          <div class="landing-cta__actions">
            <a class="ap-btn ap-btn--primary" href="${PORTAL}#/register">${icon("sparkles", 15)} ${t("Get started free")}</a>
            <a class="ap-btn ap-btn--ghost" href="${PORTAL}#/login">${t("Sign in")}</a>
          </div>
        </div>
      </section>

      <footer class="landing-foot">
        <div class="landing-foot__inner">
          <span>© ${new Date().getFullYear()} Aspect</span>
          <nav class="landing-foot__links" aria-label="${t("Footer")}">
            <a href="${PORTAL}#/login">${t("Studio")}</a>
            <a href="${PORTAL}#/support">${t("Support")}</a>
            <a href="#features">${t("Features")}</a>
          </nav>
        </div>
      </footer>
    </div>
  `;
}
